import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ApiError, formKey } from '@rydres/api-client';
import { BRAND, tcpLabel } from '@rydres/shared';
import { Button, Field, Input, Notice, Spinner, TextArea, fmtPhone, formatUsd, toE164 } from '@rydres/ui';
import { api, STRIPE_PUBLISHABLE_KEY } from '../lib/api.js';
import type { PublicBookingView, PublicPage } from '../lib/types.js';
import { AppBadges, appLinksShown } from './AppBadges.js';
import { Backups } from './Backups.js';
import { usePassenger } from '../lib/passenger.js';
import { EMPTY_TRIP, TripFields, toIso, useSlotWarning } from './TripFields.js';

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

export function BookingPage() {
  const { handle = '' } = useParams();
  const [page, setPage] = useState<PublicPage | null | undefined>(undefined);
  useEffect(() => {
    api
      .call<PublicPage>('GET', `/public/pages/${encodeURIComponent(handle)}`, { auth: false })
      .then(setPage, () => setPage(null));
  }, [handle]);

  if (page === undefined) return <Center><Spinner /></Center>;
  if (page === null) return <Center><p className="text-muted">There is no booking page at this address.</p></Center>;
  return <Page page={page} />;
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-6">{children}</div>;
}

function Page({ page }: { page: PublicPage }) {
  const navigate = useNavigate();
  // ?service=<id> or custom: chosen on the passenger's Services page.
  const wanted = useSearchParams()[0].get('service');
  const [serviceId, setServiceId] = useState(wanted === 'custom' ? 'custom' : (page.services.find((s) => s.id === wanted)?.id ?? page.services[0]?.id ?? ''));
  const custom = serviceId === 'custom';
  const service = custom ? undefined : (page.services.find((s) => s.id === serviceId) ?? page.services[0]);
  const [description, setDescription] = useState('');
  const canCard = page.acceptedModes.includes('card') && Boolean(stripePromise);
  const canDirect = page.acceptedModes.some((m) => m !== 'card');
  const [mode, setMode] = useState<'card' | 'direct'>(canCard ? 'card' : 'direct');
  const [trip, setTrip] = useState(EMPTY_TRIP);
  const [form, setForm] = useState({
    riderName: '',
    riderPhone: '',
    riderEmail: '',
    notes: '',
  });
  const passenger = usePassenger();
  const [shareMine, setShareMine] = useState(true);
  useEffect(() => {
    const me = passenger.me;
    if (me) setForm((f) => ({ ...f, riderName: f.riderName || me.details.name || '', riderPhone: f.riderPhone || me.phone, riderEmail: f.riderEmail || me.details.email || '' }));
  }, [passenger.me]);
  const [error, setError] = useState<string | null>(null);
  const [sending, setBusy] = useState(false);
  const keyFor = useRef(formKey()).current;
  const [paying, setPaying] = useState<PublicBookingView | null>(null);
  const slotWarning = useSlotWarning(page, trip.startsAt, service?.durationMin ?? 60);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const places = [...(passenger.me?.places ?? []), ...page.places].filter(
    (p, i, all) => all.findIndex((q) => q.label === p.label && q.address === p.address) === i,
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const phone = toE164(form.riderPhone);
    if (!phone) return setError('Enter a valid mobile number so the driver can reach you.');
    if (!trip.startsAt) return setError('Pick a pickup date and time.');
    if (slotWarning) return setError(slotWarning);
    if (!custom && !service) return;
    setBusy(true);
    try {
      const common = {
        riderName: form.riderName,
        riderPhone: phone,
        riderEmail: form.riderEmail || null,
        passengers: Number(trip.passengers),
        pickupAddress: trip.pickupAddress,
        dropoffAddress: trip.dropoffAddress,
        pickupPoint: trip.pickupPoint,
        dropoffPoint: trip.dropoffPoint,
        startsAt: toIso(trip.startsAt),
        notes: form.notes || null,
      };
      const body = custom ? { ...common, description: description.trim() || undefined } : { ...common, serviceId: service!.id, paymentMode: mode };
      const r = await api.call<PublicBookingView>('POST', `/public/pages/${encodeURIComponent(page.handle)}/${custom ? 'quotes' : 'bookings'}`, {
        auth: false,
        idempotencyKey: keyFor(body),
        body,
      });
      if (passenger.me && shareMine) await passenger.share(page.handle, true).catch(() => undefined);
      if (r.clientSecret) setPaying(r);
      else navigate(`/b/${r.booking.manageToken}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const vehicle = [page.vehicle.year, page.vehicle.color, page.vehicle.make, page.vehicle.model].filter(Boolean).join(' ');

  return (
    <div className="mx-auto max-w-xl px-4 pb-16 pt-6">
      <header className="flex items-start gap-4">
        {page.photoUrl ? (
          <img src={page.photoUrl} alt="" className="h-16 w-16 rounded-2xl object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-2xl font-black text-on-brand">
            {(page.displayName ?? '?').slice(0, 1)}
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-black leading-tight">{page.displayName}</h1>
          <p className="text-sm text-ink2">{page.legalName}</p>
          <p className="text-xs text-muted">{tcpLabel(page.tcpNumber)}{vehicle ? ` · ${vehicle}` : ''}{page.vehicle.seats ? ` · up to ${page.vehicle.seats} passengers` : ''}</p>
        </div>
      </header>
      {page.bio && <p className="mt-4 text-sm text-ink2">{page.bio}</p>}
      {page.serviceArea && <p className="mt-1 text-xs text-muted">Serving {page.serviceArea}</p>}
      {page.vehiclePhotoUrl && <img src={page.vehiclePhotoUrl} alt={vehicle || 'The car'} className="mt-4 aspect-[16/9] w-full rounded-2xl object-cover" />}

      {!page.live && (
        <div className="mt-6">
          <Notice tone="warn">This page is not taking bookings right now. Call {fmtPhone(page.phone)} instead.</Notice>
          <Backups list={page.backups} driverName={page.displayName} />
        </div>
      )}

      {page.live && !paying && (
        <form onSubmit={submit} className="mt-6 space-y-5">
          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">Choose a service</h2>
            <div className="space-y-2">
              {page.services.map((s) => (
                <label
                  key={s.id}
                  className={`flex cursor-pointer items-start justify-between gap-3 rounded-2xl border p-4 ${serviceId === s.id ? 'border-accent bg-accent-soft/40 ring-1 ring-accent' : 'border-line bg-card'}`}
                >
                  <input type="radio" className="sr-only" name="service" checked={serviceId === s.id} onChange={() => setServiceId(s.id)} />
                  <div>
                    <div className="font-bold">{s.name}</div>
                    {s.description && <div className="text-sm text-ink2">{s.description}</div>}
                    <div className="mt-1 text-xs text-muted">Up to {s.maxPassengers} passengers · per vehicle</div>
                  </div>
                  <div className="text-lg font-black">{formatUsd(s.priceCents)}</div>
                </label>
              ))}
              <label className={`flex cursor-pointer items-start justify-between gap-3 rounded-2xl border p-4 ${custom ? 'border-accent bg-accent-soft/40 ring-1 ring-accent' : 'border-line bg-card'}`}>
                <input type="radio" className="sr-only" name="service" checked={custom} onChange={() => setServiceId('custom')} />
                <div>
                  <div className="font-bold">Something else</div>
                  <div className="text-sm text-ink2">Hourly, multiple stops, an event. Describe it and {page.displayName} will text you a price.</div>
                </div>
                <div className="text-sm font-bold text-muted">Quote</div>
              </label>
            </div>
          </section>
          {custom && (
            <Field label="Tell the driver about the trip (optional)" hint="Fill in the pickup, drop-off and time below; add anything that affects the price here.">
              <TextArea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Anything that affects the price: hours, stops, waiting, luggage, car seats." />
            </Field>
          )}

          <section className="grid gap-3 sm:grid-cols-2">
            <TripFields
              page={page}
              form={trip}
              onChange={setTrip}
              service={service}
              places={places}
              warning={slotWarning}
              warningExtra={<Backups list={page.backups} driverName={page.displayName} title={`Or book a driver ${page.displayName ?? 'this driver'} trusts`} />}
              placesFooter={
                passenger.open && !passenger.me && !passenger.loading ? (
                  <p className="text-xs text-muted">
                    <Link className="font-bold underline" to={`/me?back=/${encodeURIComponent(page.handle)}`}>Sign in</Link> to use your saved places.
                  </p>
                ) : undefined
              }
            />
            <Field label="Your name">
              <Input required autoComplete="name" value={form.riderName} onChange={(e) => set('riderName', e.target.value)} />
            </Field>
            <Field label="Mobile number" note="For booking texts from the driver (confirmation, reminder, changes)">
              <Input required inputMode="tel" autoComplete="tel" value={form.riderPhone} onChange={(e) => set('riderPhone', e.target.value)} />
            </Field>
            <Field label="Email (optional)" hint="For your receipt">
              <Input type="email" autoComplete="email" value={form.riderEmail} onChange={(e) => set('riderEmail', e.target.value)} />
            </Field>
            {passenger.me && (
              <label className="flex items-start gap-2 text-sm text-ink2">
                <input type="checkbox" className="mt-1" checked={shareMine} onChange={(e) => setShareMine(e.target.checked)} />
                <span>Share my {BRAND.name} details with {page.displayName}, so they stay up to date for my next ride</span>
              </label>
            )}
            <Field label="Notes (optional)">
              <TextArea placeholder="Flight number, bags, child seat…" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </Field>
          </section>

          {!custom && canCard && canDirect && (
            <section>
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">How you'll pay</h2>
              <div className="flex gap-2 rounded-2xl bg-card2 p-1">
                {(['card', 'direct'] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)} className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold ${mode === m ? 'bg-card shadow-card' : 'text-muted'}`}>
                    {m === 'card' ? 'Card now' : 'Pay the driver directly'}
                  </button>
                ))}
              </div>
              {mode === 'direct' && (
                <p className="mt-2 text-xs text-muted">
                  The driver confirms your request and you pay them {page.zelleHandle ? `by Zelle (${page.zelleHandle}), cash` : 'in cash'} or as agreed.
                </p>
              )}
            </section>
          )}

          <div className="rounded-2xl border border-line bg-card p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-ink2">{custom ? 'Custom trip' : service?.name}</span>
              <span className="text-xl font-black">{custom ? 'Price by text' : service ? formatUsd(service.priceCents) : ''}</span>
            </div>
            <p className="mt-2 text-xs text-muted">{page.cancelPolicyText}</p>
            <p className="mt-1 text-xs text-muted">
              Your contract is with {page.legalName ?? page.displayName}, a licensed California charter-party carrier ({tcpLabel(page.tcpNumber)}). {BRAND.name} provides the booking software only.
            </p>
            <p className="mt-2 text-xs text-muted">
              By booking you agree to receive text messages about this booking from your driver through {BRAND.name} (confirmation, reminder, changes). Message and data rates may apply. Message frequency varies by booking. Reply STOP to stop, HELP for help. See our <a className="underline" href="/privacy">privacy policy</a> and <a className="underline" href="/terms">terms</a>.
            </p>
          </div>

          {error && <Notice tone="danger">{error}</Notice>}
          <Button type="submit" disabled={sending || (!custom && !service) || Boolean(slotWarning)} className="w-full">
            {sending ? 'One moment…' : custom ? 'Ask for a price' : mode === 'card' ? `Continue to payment · ${service ? formatUsd(service.priceCents) : ''}` : 'Request this ride'}
          </Button>
        </form>
      )}

      {paying && stripePromise && (
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl border border-line bg-card p-4 text-sm">
            <div className="font-bold">{service?.name}</div>
            <div className="text-ink2">{new Date(paying.booking.startsAt).toLocaleString()} · {paying.booking.pickupAddress} → {paying.booking.dropoffAddress}</div>
            <div className="mt-1 text-lg font-black">{formatUsd(paying.booking.priceCents ?? 0)}</div>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret: paying.clientSecret!, appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb', borderRadius: '12px' } } }}>
            <PayForm view={paying} />
          </Elements>
          <button type="button" className="w-full text-sm text-muted" onClick={() => setPaying(null)}>
            Back
          </button>
        </div>
      )}

      {appLinksShown() && (
        <div className="mt-10 flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-muted">Book {page.displayName ?? 'this driver'} faster next time with the {BRAND.name} app</p>
          <AppBadges small />
        </div>
      )}
      <footer className="mt-12 text-center text-xs text-muted">
        Questions? Call {fmtPhone(page.phone)}. Powered by {BRAND.name}, operated by {BRAND.operator} · <a className="underline" href="/privacy">Privacy Policy</a> · <a className="underline" href="/terms">Terms of Use</a>
      </footer>
    </div>
  );
}

function PayForm({ view }: { view: PublicBookingView }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pay = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const { error: err } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${location.origin}/b/${view.booking.manageToken}?paid=1` },
    });
    if (err) {
      setError(err.message ?? 'Payment did not go through.');
      setBusy(false);
    }
  };
  return (
    <form onSubmit={pay} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <Notice tone="danger">{error}</Notice>}
      <Button type="submit" disabled={busy || !stripe} className="w-full">
        {busy ? 'Processing…' : `Pay ${formatUsd(view.booking.priceCents ?? 0)}`}
      </Button>
      <p className="text-center text-xs text-muted">Charged by {view.driver.legalName ?? view.driver.displayName}. Apple Pay and Google Pay appear when your device supports them.</p>
    </form>
  );
}
